import { useEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

const STORAGE_KEY = "scroll-positions";

const readPositions = (): Record<string, number> => {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
};

const writePosition = (key: string, value: number) => {
  try {
    const positions = readPositions();
    positions[key] = value;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(positions));
  } catch {
    /* ignore */
  }
};

const ScrollToHash = () => {
  const location = useLocation();
  const navigationType = useNavigationType();

  // Disable the browser's own restoration so ours is the only one
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  // Continuously record the scroll position for the current entry
  useEffect(() => {
    const key = location.key || location.pathname;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => writePosition(key, window.scrollY));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      writePosition(key, window.scrollY);
    };
  }, [location.key, location.pathname]);

  useEffect(() => {
    const hash = location.hash;

    if (hash) {
      const scroll = () => {
        const id = hash.replace("#", "");
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      };

      requestAnimationFrame(scroll);

      let attempts = 0;
      const interval = setInterval(() => {
        attempts += 1;
        scroll();
        if (attempts > 10) clearInterval(interval);
      }, 100);

      return () => clearInterval(interval);
    }

    if (navigationType === "POP") {
      // Back/forward: restore the previous position for this history entry
      const key = location.key || location.pathname;
      const saved = readPositions()[key];
      const target = typeof saved === "number" ? saved : 0;

      let attempts = 0;
      const restore = () => {
        window.scrollTo(0, target);
        attempts += 1;
        if (attempts > 12 || Math.abs(window.scrollY - target) < 2) {
          clearInterval(interval);
        }
      };
      requestAnimationFrame(restore);
      const interval = setInterval(restore, 60);
      return () => clearInterval(interval);
    }

    // New navigation - start at the top
    window.scrollTo(0, 0);
  }, [location.key, location.pathname, location.hash, navigationType]);

  return null;
};

export default ScrollToHash;
